import {
  Badge,
  Button,
  CallMdIcon,
  CopyMdIcon,
  DescriptionMdIcon,
  IconButton,
  LocationMdIcon,
  MoreMdIcon,
  TimeSmIcon,
} from '@crono-one/design-system';
import { format, parseISO } from 'date-fns';
import { useEffect, useState } from 'react';
import { Event } from 'crono-fe-common/types/event';
import { generateEventCrmLink } from 'crono-fe-common/utils/crm-utils';
import { getMeetingInfo } from 'pages/accountDrawer/utils/meeting.helpers';
import { useMeetingActions } from 'pages/accountDrawer/hooks/useMeetingActions';
import InfoRow from './InfoRow';
import AttendeeRow from './AttendeeRow';
import MeetingLinkRows from './MeetingLinkRows';
import MeetingOutcomeField from './MeetingOutcomeField';
import MeetingDropdownActions from 'pages/accountDrawer/components/MeetingDropdownActions';
import { MeetingActionsProvider } from 'pages/accountDrawer/hooks/meetingActionsContext';
import {
  formatMeetingDuration,
  formatMeetingTimeRange,
  toUtcCalendarDay,
} from 'utils/dateUtils';
import useGetUsers from 'hooks/services/subscription/useGetUsers';
import { useAuth } from 'context/auth';
import useGetEvent from 'hooks/services/event/useGetEvent';
import { useAccountDrawer } from 'context/accountDrawer';
import { toast } from '@crono-one/design-system/side-effects';

const copyToClipboard = async (text: string, label: string) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  } catch {
    toast.error(`Failed to copy ${label}`);
  }
};
interface MeetingHeaderActionsProps {
  event?: Event | null;
  disabled?: boolean;
}

const MeetingHeaderActions = ({
  event,
  disabled,
}: MeetingHeaderActionsProps) => {
  const { joinAction, isFutureMeeting } = useMeetingActions(event);

  return (
    <div className="min-w-40 flex items-center justify-end gap-3">
      {isFutureMeeting ? (
        <Button disabled={disabled || !joinAction} onClick={joinAction?.action}>
          Join meeting
        </Button>
      ) : (
        <p className="text-xs font-medium text-gray-700">
          Meeting was executed
        </p>
      )}

      <MeetingDropdownActions
        event={event}
        trigger={
          <IconButton
            icon={MoreMdIcon}
            label="Open more actions"
            variant="outline"
            shape="rounded"
            disabled={disabled}
          />
        }
      />
    </div>
  );
};

interface IMeetingViewProps {
  activity?: Event | null;
}

export const MeetingView = (props: IMeetingViewProps) => {
  const { user } = useAuth();
  const { closeDrawer } = useAccountDrawer();

  const { activity } = props;
  const [liveActivity, setLiveActivity] = useState<typeof activity | null>(
    null,
  );
  const displayActivity = liveActivity ?? activity;

  const { data: eventDetails, isLoading } = useGetEvent(displayActivity?.id);

  const meetingInfo = getMeetingInfo(eventDetails);

  useEffect(() => {
    setLiveActivity(null);
  }, [activity?.id]);

  // Whether a calendar event backs the meeting. Independent from being synced to
  // the CRM: a meeting can have both, either or neither, and each one contributes
  // its own row of links.
  const isCalendarSynced = !!eventDetails?.meeting;

  // isSync is false both when the meeting is not in the CRM and when the CRM has
  // no page for it, in which case the link is a placeholder message rather than a
  // URL — only a real link is worth a row.
  const eventCrmLink = generateEventCrmLink(eventDetails ?? null, user ?? null);
  const crmUrl = eventCrmLink.isSync ? eventCrmLink.value : null;

  const { data } = useGetUsers();
  const subscriptionUsers = new Set(
    data?.data?.data.map((user) => user.email) ?? [],
  );

  const startDate = meetingInfo?.startTime
    ? parseISO(meetingInfo.startTime)
    : null;
  const endDate = meetingInfo?.endTime ? parseISO(meetingInfo.endTime) : null;
  // All-day meetings are floating calendar dates stored as UTC midnight, so we
  // read the canonical UTC day. Timed meetings are real instants and stayin
  // the viewer's timezone.
  const headerDate =
    startDate && meetingInfo?.isAllDay
      ? toUtcCalendarDay(startDate)
      : startDate;
  const month = headerDate ? format(headerDate, 'MMM') : '—';
  const day = headerDate ? format(headerDate, 'd') : '—';
  const year = headerDate ? format(headerDate, 'yyyy') : '—';

  const allDayRange =
    meetingInfo?.isAllDay && startDate && endDate
      ? (() => {
          const start = format(toUtcCalendarDay(startDate), 'd MMMM');
          const end = format(toUtcCalendarDay(endDate), 'd MMMM');
          return start !== end ? `${start} - ${end}` : null;
        })()
      : null;

  // A meeting logged without an end has a start worth showing on its own.
  let timeRange = '—';
  if (meetingInfo?.startTime && meetingInfo.endTime) {
    timeRange = formatMeetingTimeRange(
      meetingInfo.startTime,
      meetingInfo.endTime,
    );
  } else if (startDate) {
    timeRange = format(startDate, 'HH:mm');
  }

  const duration =
    meetingInfo?.startTime && meetingInfo?.endTime
      ? formatMeetingDuration(meetingInfo.startTime, meetingInfo.endTime)
      : null;

  const phoneNumber = meetingInfo?.conferencePhoneNumber;
  const phonePin = meetingInfo?.conferencePhonePin;
  let phoneText: string | null = null;
  if (phoneNumber && phonePin) {
    phoneText = `${phoneNumber} PIN: ${phonePin}`;
  } else if (phoneNumber) {
    phoneText = phoneNumber;
  }

  return (
    // Hosts the editor, the reschedule dialog and the delete flow the actions
    // menu below drives.
    <MeetingActionsProvider onMeetingDeleted={closeDrawer}>
      <div className="py-4 flex flex-col gap-6 overflow-hidden">
        <div className="flex items-start justify-between px-6">
          <div className="flex-1 min-w-0 flex items-center gap-6 mr-4">
            {isLoading ? (
              <div className="w-18 h-20 rounded-xl bg-neutral-300 animate-pulse shrink-0" />
            ) : (
              <div className="flex flex-col p-1.5 bg-neutral-100 rounded-xl gap-1 w-18">
                <div className="text-xs text-neutral-800 text-center">
                  {month}
                </div>

                <div className="text-2xl font-medium text-center text-neutral-800">
                  {day}
                </div>

                <div className="text-neutral-600 bg-white text-xs py-0.5 text-center rounded-md">
                  {year}
                </div>
              </div>
            )}

            <div className="flex-1 min-w-0 flex flex-col gap-3">
              <div className="text-lg font-bold">{meetingInfo?.title}</div>

              <div className="flex flex-col gap-0.5">
                <div className="flex items-center gap-2">
                  <TimeSmIcon className="size-4 text-neutral-800" />

                  {meetingInfo?.isAllDay ? (
                    <span className="text-neutral-800 text-sm">
                      {allDayRange ? `All day (${allDayRange})` : 'All day'}
                    </span>
                  ) : (
                    <>
                      <span className="text-neutral-800 text-sm">
                        {timeRange}
                      </span>

                      {duration && <Badge tone="lightGray">{duration}</Badge>}
                    </>
                  )}
                </div>

                <MeetingLinkRows
                  meetingInfo={meetingInfo}
                  isCalendarSynced={isCalendarSynced}
                  crmUrl={crmUrl}
                  isLoading={isLoading}
                  onCopy={copyToClipboard}
                />
              </div>
            </div>
          </div>

          <MeetingHeaderActions event={eventDetails} disabled={isLoading} />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 flex-1 overflow-y-auto xl:overflow-hidden px-6">
          <div className="col-span-2 grid grid-cols-2 gap-4 p-4 border border-neutral-300 rounded-2xl">
            <MeetingOutcomeField event={eventDetails} isLoading={isLoading} />
          </div>

          <div className="flex flex-col gap-2.5 overflow-hidden p-4 border border-neutral-300 rounded-2xl">
            <p className="text-sm font-bold">Info</p>

            <div className="flex flex-col gap-6.5 overflow-hidden">
              <InfoRow
                icon={CallMdIcon}
                title="Phone"
                valueWrapperClassName="w-full"
                isLoading={isLoading}
                value={phoneText ?? '-'}
                titleSuffix={
                  phoneText ? (
                    <IconButton
                      variant="text"
                      icon={CopyMdIcon}
                      label="Copy phone number to clipboard"
                      onClick={() => copyToClipboard(phoneText, 'Phone number')}
                    />
                  ) : undefined
                }
              />

              <InfoRow
                icon={LocationMdIcon}
                title="Location"
                valueWrapperClassName="w-full"
                isLoading={isLoading}
                value={meetingInfo?.location || '-'}
                titleSuffix={
                  meetingInfo?.location ? (
                    <IconButton
                      variant="text"
                      icon={CopyMdIcon}
                      label="Copy location to clipboard"
                      onClick={() =>
                        copyToClipboard(meetingInfo.location!, 'Location')
                      }
                    />
                  ) : undefined
                }
              />

              <InfoRow
                icon={DescriptionMdIcon}
                title="Description"
                wrapperClassName="flex-1"
                valueWrapperClassName="block overflow-auto"
              >
                {meetingInfo?.description ? (
                  <div
                    className="text-xs text-neutral-800 [&_a]:text-primary-500 [&_a]:underline "
                    dangerouslySetInnerHTML={{
                      __html: meetingInfo.description,
                    }}
                  />
                ) : (
                  <p className="text-xs text-neutral-800">-</p>
                )}
              </InfoRow>
            </div>
          </div>

          <div className="flex flex-col gap-1 overflow-hidden p-4 border border-neutral-300 rounded-2xl h-fit xl:h-full">
            <p className="text-sm font-bold">Attendees</p>

            <div className="flex flex-col gap-2 xl:overflow-y-auto">
              {!meetingInfo?.attendeeEmails?.length && !isLoading && (
                <p className="text-sm text-neutral-800">No attendees found</p>
              )}
              {isLoading ? (
                <>
                  <div className="h-8 rounded-lg bg-neutral-300 animate-pulse" />
                  <div className="h-8 rounded-lg bg-neutral-300 animate-pulse" />
                  <div className="h-8 rounded-lg bg-neutral-300 animate-pulse" />
                </>
              ) : (
                meetingInfo?.attendeeEmails?.map((email) => (
                  <AttendeeRow
                    key={email}
                    email={email}
                    isOrganizer={email === meetingInfo?.organizerEmail}
                    isSubscriptionUser={subscriptionUsers.has(email)}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </MeetingActionsProvider>
  );
};
